import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Send, Bell, Download, ExternalLink, DollarSign, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function InvoiceStatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
    sent: { label: 'Sent', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    paid: { label: 'Paid', className: 'bg-green-500/20 text-green-300 border-green-500/30' },
    overdue: { label: 'Overdue', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <Badge variant="outline" className={`${config.className} text-sm px-3 py-1`}>
      {config.label}
    </Badge>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [client, setClient] = useState(null);
  const [toEmail, setToEmail] = useState('');
  const [ccMe, setCcMe] = useState(true);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [loading, setLoading] = useState(true);

  async function loadInvoice() {
    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInv(data);
        // Load client
        if (data.client_id) {
          const clientRes = await fetch(`${API_URL}/api/clients`, {
            credentials: 'include'
          });
          if (clientRes.ok) {
            const clientData = await clientRes.json();
            // Handle both array response and {clients: [...]} format
            const clientList = Array.isArray(clientData) ? clientData : (clientData.clients || []);
            const c = clientList.find(cl => cl.id === data.client_id);
            setClient(c);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInvoice();
  }, [id]);

  async function sendInvoice() {
    try {
      setSending(true);
      const response = await fetch(`${API_URL}/api/invoices/${id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to_email: toEmail || undefined,
          cc_user_email: ccMe
        })
      });
      const data = await response.json();
      if (response.ok) {
        setInv(data);
        toast.success('Invoice sent!');
      } else {
        toast.error(data.detail || 'Failed to send invoice');
      }
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  }

  async function sendReminder() {
    try {
      setReminding(true);
      const response = await fetch(`${API_URL}/api/invoices/${id}/remind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cc_user_email: ccMe,
          to_email: toEmail || undefined
        })
      });
      if (response.ok) {
        toast.success('Reminder sent!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send reminder');
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setReminding(false);
    }
  }

  function exportPdf() {
    window.open(`${API_URL}/api/invoices/${id}/export-pdf`, '_blank');
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="p-6 text-center text-slate-400">
        <p>Invoice not found</p>
        <Button variant="link" onClick={() => navigate('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  const canRemind = inv.status === 'sent' && !inv.paid_at;
  const canSend = inv.status === 'draft' || !inv.stripe_checkout_url;

  return (
    <div className="p-6 space-y-6" data-testid="invoice-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-6 h-6 text-indigo-400" />
            Invoice
            <InvoiceStatusBadge status={inv.status} />
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={exportPdf}
          className="border-slate-600 text-white hover:bg-slate-700"
        >
          <Download className="w-4 h-4 mr-2" />
          Export PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Summary */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <div className="text-slate-400 text-sm mb-1">Client</div>
                <div className="text-white font-medium">{client?.name || 'Unknown'}</div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <div className="text-slate-400 text-sm mb-1">Total Amount</div>
                <div className="text-2xl font-bold text-white flex items-center gap-1">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  {(inv.amount_total || 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <div className="text-slate-400 text-sm mb-1">Due Date</div>
                <div className="text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                </div>
              </div>
              {inv.stripe_checkout_url && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="text-slate-400 text-sm mb-1">Payment Link</div>
                  <a
                    href={inv.stripe_checkout_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Payment
                  </a>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div>
              <h3 className="text-white font-medium mb-3">Line Items</h3>
              <div className="bg-slate-900/50 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3 text-slate-400 font-medium">Item</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Qty</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Rate</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inv.line_items || []).map((li, i) => (
                      <tr key={i} className="border-b border-slate-700/50">
                        <td className="p-3 text-white">{li.item}</td>
                        <td className="p-3 text-slate-300 text-right">{li.quantity}</td>
                        <td className="p-3 text-slate-300 text-right">${(li.rate || 0).toFixed(2)}</td>
                        <td className="p-3 text-white text-right font-medium">
                          ${(li.amount || li.quantity * li.rate).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-right text-sm text-slate-400">
                <span className="mr-4">Discount: {inv.discount_percent || 0}%</span>
                <span>Tax: {inv.tax_percent || 0}%</span>
              </div>
            </div>

            {inv.notes && (
              <div>
                <h3 className="text-white font-medium mb-2">Notes</h3>
                <p className="text-slate-300 text-sm bg-slate-900/50 rounded-lg p-4">
                  {inv.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Invoice Card */}
        <Card className="bg-slate-800/50 border-slate-700 h-fit">
          <CardHeader>
            <CardTitle className="text-white">Send Invoice</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="toEmail" className="text-slate-300">
                Recipient Email (optional)
              </Label>
              <Input
                id="toEmail"
                placeholder={client?.email || "Client's email"}
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={ccMe}
                onChange={(e) => setCcMe(e.target.checked)}
                className="rounded border-slate-600"
              />
              CC me a copy
            </label>
            <div className="flex flex-col gap-2">
              {canSend && (
                <Button
                  onClick={sendInvoice}
                  disabled={sending}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending…' : 'Send Invoice'}
                </Button>
              )}
              {canRemind && (
                <Button
                  onClick={sendReminder}
                  disabled={reminding}
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-700 w-full"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {reminding ? 'Sending…' : 'Send Reminder'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
